import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Save } from 'lucide-react';
import { eventService, locationService } from '../../services';
import type { Event, Location } from '../../types';

const EditEventPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    event_camera: '',
    event_type: '',
    event_tag: '',
    event_note: '',
    event_state: '',
    event_location: '',
    confidence: '',
  });

  useEffect(() => {
    fetchLocations();
    if (id) {
      fetchEvent();
    }
  }, [id]);

  const fetchEvent = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await eventService.getEventById(parseInt(id!));
      setEvent(data);
      setFormData({
        event_camera: data.event_camera || '',
        event_type: data.event_type || '',
        event_tag: data.event_tag || '',
        event_note: data.event_note || '',
        event_state: data.event_state || '',
        event_location: data.event_location?.toString() || '',
        confidence: data.confidence?.toString() || '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch event');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getLocations();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event) return;

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      const payload: any = {
        event_camera: formData.event_camera || undefined,
        event_type: formData.event_type || undefined,
        event_tag: formData.event_tag || undefined,
        event_note: formData.event_note || undefined,
        event_state: formData.event_state || undefined,
        event_location: formData.event_location ? parseInt(formData.event_location) : undefined,
        confidence: formData.confidence ? parseFloat(formData.confidence) : undefined,
      };

      await eventService.updateEvent(event.event_id, payload);
      setSuccessMessage('Event updated successfully!');
      setTimeout(() => {
        navigate(`/events/${event.event_id}/view`);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update event');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Loading size="lg" text="Loading event..." />
      </MainLayout>
    );
  }

  if (error && !event) {
    return (
      <MainLayout>
        <Alert variant="error" title="Error" message={error} />
      </MainLayout>
    );
  }

  const eventTypeOptions = [
    { label: 'Select Type', value: '' },
    { label: 'Motion Detection', value: 'motion_detection' },
    { label: 'Line Crossing', value: 'line_crossing' },
    { label: 'Intrusion', value: 'intrusion' },
    { label: 'Face Detection', value: 'face_detection' },
    { label: 'License Plate', value: 'license_plate' },
    { label: 'Tampering', value: 'tampering' },
    { label: 'Video Loss', value: 'video_loss' },
    { label: 'Other', value: 'other' },
  ];

  const eventStateOptions = [
    { label: 'Select State', value: '' },
    { label: 'Active', value: 'active' },
    { label: 'Resolved', value: 'resolved' },
    { label: 'Dismissed', value: 'dismissed' },
  ];

  const locationOptions = [
    { label: 'Select Location', value: '' },
    ...locations.map(loc => ({ label: loc.ycl_name, value: loc.ycl_id.toString() })),
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate(`/events/${event?.event_id}/view`)}
            >
              Back to Event
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Edit Event</h1>
              <p className="text-sm text-text-muted mt-1">
                Event #{event?.event_id}
              </p>
            </div>
          </div>
        </div>

        {successMessage && (
          <Alert variant="success" title="Success" message={successMessage} />
        )}

        {error && (
          <Alert variant="error" title="Error" message={error} />
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <h2 className="text-lg font-semibold text-text-primary mb-6">Event Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Camera"
                name="event_camera"
                value={formData.event_camera}
                onChange={handleInputChange}
                placeholder="Camera identifier"
              />

              <Select
                label="Event Type"
                name="event_type"
                value={formData.event_type}
                onChange={handleInputChange}
                options={eventTypeOptions}
              />

              <Input
                label="Event Tag"
                name="event_tag"
                value={formData.event_tag}
                onChange={handleInputChange}
                placeholder="Event tag or category"
              />

              <Select
                label="Event State"
                name="event_state"
                value={formData.event_state}
                onChange={handleInputChange}
                options={eventStateOptions}
              />

              <Select
                label="Location"
                name="event_location"
                value={formData.event_location}
                onChange={handleInputChange}
                options={locationOptions}
              />

              <Input
                label="Confidence"
                name="confidence"
                type="number"
                value={formData.confidence}
                onChange={handleInputChange}
                placeholder="0.0 to 1.0"
                step="0.01"
                min="0"
                max="1"
              />

              <div className="md:col-span-2">
                <Textarea
                  label="Notes"
                  name="event_note"
                  value={formData.event_note}
                  onChange={handleInputChange}
                  placeholder="Event notes or description..."
                  rows={4}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-surface-secondary">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/events/${event?.event_id}/view`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                leftIcon={<Save size={18} />}
                isLoading={isSaving}
              >
                Save Changes
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditEventPage;
